<template>
  <div class="nav-page">
    <!-- 导航分组 -->
    <div v-if="navGroups.length === 0" class="empty-tip">
      暂无导航站点，点击右上角「管理」添加
    </div>
    <div v-for="group in navGroups" :key="group.group" class="nav-group">
      <div class="group-title">{{ group.group }}</div>
      <div class="group-sites">
        <a
          v-for="site in group.sites"
          :key="site.id"
          :href="site.url"
          target="_blank"
          rel="noopener noreferrer"
          class="site-link"
        >
          <el-card shadow="hover" class="site-card">
            <img v-if="site.icon_url && !iconErrors[site.id]" :src="proxyIcon(site.icon_url)" class="site-icon" @error="$set(iconErrors, site.id, true)" />
            <span v-else class="site-icon-fallback">{{ site.name.charAt(0).toUpperCase() }}</span>
            <div class="site-info">
              <div class="site-name">{{ site.name }}</div>
              <div class="site-desc">{{ site.description }}</div>
            </div>
          </el-card>
        </a>
      </div>
    </div>

    <!-- 管理按钮 -->
    <el-button
      type="primary"
      icon="el-icon-setting"
      class="manage-btn"
      @click="openManage"
    >管理</el-button>

    <!-- 管理对话框 -->
    <el-dialog title="导航管理" :visible.sync="manageVisible" width="700px">
      <!-- 新增表单 -->
      <el-form :model="form" label-width="80px" size="small">
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="分组名">
              <el-autocomplete
                v-model="form.group_name"
                :fetch-suggestions="queryGroups"
                placeholder="选择或输入新分组"
                style="width:100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="站点名">
              <el-input v-model="form.name" placeholder="如：Portainer" />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="站点地址">
              <el-input v-model="form.url" placeholder="https://portainer.example.com" @input="autoFillIcon" />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="图标URL">
              <el-input v-model="form.icon_url" placeholder="自动填充，可手动修改">
                <template slot="append">
                  <el-tooltip content="切换为 favicon.im 格式">
                    <el-button icon="el-icon-refresh" @click="switchToFaviconIm" />
                  </el-tooltip>
                  <el-tooltip content="切换为 /favicon.ico 格式">
                    <el-button icon="el-icon-link" @click="switchToFaviconIco" />
                  </el-tooltip>
                  <el-tooltip :content="iconTestResult">
                    <el-button icon="el-icon-search" @click="testIconUrl" />
                  </el-tooltip>
                </template>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="16">
            <el-form-item label="描述">
              <el-input v-model="form.description" placeholder="简短描述" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="排序">
              <el-input-number v-model="form.sort_order" :min="0" style="width:100%" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item>
          <el-button type="primary" size="small" @click="submitForm">{{ editingId ? '保存修改' : '新增站点' }}</el-button>
          <el-button size="small" @click="resetForm">重置</el-button>
        </el-form-item>
      </el-form>

      <el-divider />

      <!-- 站点列表 -->
      <el-table :data="allSites" size="small" max-height="300">
        <el-table-column label="分组" prop="group_name" width="100" />
        <el-table-column label="图标" width="60">
          <template #default="{ row }">
            <img v-if="row.icon_url" :src="proxyIcon(row.icon_url)" style="width:20px;height:20px;object-fit:contain;" @error="$event.target.style.display='none'" />
          </template>
        </el-table-column>
        <el-table-column label="站点名" prop="name" width="120" />
        <el-table-column label="地址" prop="url" show-overflow-tooltip />
        <el-table-column label="操作" width="120">
          <template #default="{ row }">
            <el-button type="text" size="mini" @click="editSite(row)">编辑</el-button>
            <el-button type="text" size="mini" style="color:#f56c6c" @click="deleteSite(row.id)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-dialog>
  </div>
</template>

<script>
import axios from '@/config'

const emptyForm = () => ({ group_name: '', name: '', icon_url: '', url: '', description: '', sort_order: 0 })

function proxyIcon(url) {
  if (!url) return ''
  return '/api/favicon?url=' + encodeURIComponent(url)
}

export default {
  name: 'NavLinks',
  data() {
    return {
      navGroups: [],
      allSites: [],
      manageVisible: false,
      editingId: null,
      form: emptyForm(),
      iconErrors: {},
      iconTestResult: '点击测试图标是否可访问',
    }
  },
  mounted() {
    this.fetchSites()
  },
  methods: {
    proxyIcon,
    queryGroups(queryStr, cb) {
      const groups = this.navGroups.map(g => ({ value: g.group }))
      const results = queryStr
        ? groups.filter(g => g.value.includes(queryStr))
        : groups
      cb(results)
    },
    switchToFaviconIm() {
      try {
        const { hostname } = new URL(this.form.url)
        this.form.icon_url = `https://favicon.im/zh/${hostname}`
      } catch (_) { /* url 尚未完整，忽略解析错误 */ }
    },
    switchToFaviconIco() {
      try {
        const { protocol, hostname } = new URL(this.form.url)
        this.form.icon_url = `${protocol}//${hostname}/favicon.ico`
      } catch (_) { /* url 尚未完整，忽略解析错误 */ }
    },
    testIconUrl() {
      if (!this.form.icon_url) {
        this.iconTestResult = '请先填写图标URL'
        return
      }
      this.iconTestResult = '测试中...'
      const img = new Image()
      img.onload = () => { this.iconTestResult = '✓ 可以访问' }
      img.onerror = () => { this.iconTestResult = '✗ 无法访问' }
      img.src = proxyIcon(this.form.icon_url) + '&t=' + Date.now()
    },
    autoFillIcon(val) {
      try {
        const { protocol, hostname } = new URL(val)
        const auto = `${protocol}//${hostname}/favicon.ico`
        if (!this.form.icon_url || this.form.icon_url === this._lastAutoIcon) {
          this.form.icon_url = auto
          this._lastAutoIcon = auto
        }
      } catch (_) { /* url 尚未完整，忽略解析错误 */ }
    },
    async fetchSites() {
      const res = await axios.get('nav_sites')
      if (res.data.code === '0000') {
        this.navGroups = res.data.data
        this.allSites = res.data.data.flatMap(g => g.sites)
      }
    },
    openManage() {
      this.resetForm()
      this.manageVisible = true
    },
    editSite(row) {
      this.editingId = row.id
      this.form = { group_name: row.group_name, name: row.name, icon_url: row.icon_url, url: row.url, description: row.description, sort_order: row.sort_order }
    },
    resetForm() {
      this.editingId = null
      this.form = emptyForm()
      this._lastAutoIcon = ''
    },
    async submitForm() {
      if (!this.form.name || !this.form.url) {
        this.$message.warning('站点名和地址为必填')
        return
      }
      if (this.editingId) {
        await axios.put(`nav_sites/${this.editingId}`, this.form)
        this.$message.success('修改成功')
      } else {
        await axios.post('nav_sites', this.form)
        this.$message.success('新增成功')
      }
      this.resetForm()
      await this.fetchSites()
    },
    async deleteSite(id) {
      await this.$confirm('确认删除该站点？', '提示', { type: 'warning' })
      await axios.delete(`nav_sites/${id}`)
      this.$message.success('删除成功')
      await this.fetchSites()
    },
  },
}
</script>

<style scoped>
.nav-page {
  padding: 24px;
  position: relative;
  min-height: 200px;
}
.empty-tip {
  color: #909399;
  text-align: center;
  margin-top: 60px;
}
.nav-group {
  margin-bottom: 28px;
}
.group-title {
  font-size: 13px;
  font-weight: bold;
  color: #909399;
  letter-spacing: 1px;
  margin-bottom: 12px;
  padding-bottom: 6px;
  border-bottom: 1px solid #ebeef5;
}
.group-sites {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.site-link {
  text-decoration: none;
  color: inherit;
}
.site-card {
  width: 180px;
  cursor: pointer;
}
.site-card:deep(.el-card__body) {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px;
}
.site-icon {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  object-fit: contain;
}
.site-icon-fallback {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border-radius: 6px;
  background: #409eff;
  color: #fff;
  font-size: 14px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
}
.site-name {
  font-size: 14px;
  font-weight: 600;
}
.site-desc {
  font-size: 12px;
  color: #909399;
  margin-top: 3px;
}
.manage-btn {
  position: absolute;
  top: 24px;
  right: 24px;
}
</style>
